/**
 * Multi-business workspace hooks (Task #616).
 *
 * These hooks live in their own file (parallel to attribution-hooks.ts and
 * social-hooks.ts) instead of missing-hooks.ts so they don't bump the frozen
 * MAX_EXPORTS guard.  They wrap REST endpoints under `/api/workspaces`.
 *
 * Switching workspace:
 *   1. Call useActivateWorkspace().mutateAsync({ id }).
 *   2. Pass `queryClient.invalidateQueries()` (no key) — every cached query
 *      becomes stale, so the next refetch returns data scoped to the newly
 *      active workspace.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

/**
 * Optional sink for "the active workspace just changed" — apps can register
 * a callback (e.g. to update a sessionStorage value used by their fetch
 * helpers) so subsequent requests carry the right `X-Workspace-Id` header.
 */
let _activeWorkspaceSink: ((id: number) => void) | null = null;
export function setActiveWorkspaceSink(
  sink: ((id: number) => void) | null,
): void {
  _activeWorkspaceSink = sink;
}

export type WorkspaceSummary = {
  id: number;
  name: string;
  brandColor: string | null;
  logoUrl: string | null;
  isOwner: boolean;
  memberCount: number;
  lastActiveAt: string | null;
  createdAt: string;
};

export type ListWorkspacesResponse = {
  workspaces: WorkspaceSummary[];
  activeWorkspaceId: number | null;
};

export type CreateWorkspaceBody = {
  name: string;
  brandColor?: string | null;
  logoUrl?: string | null;
};

export type UpdateWorkspaceBody = {
  name?: string;
  brandColor?: string | null;
  logoUrl?: string | null;
};

const BASE = "/api/workspaces";

export function useListWorkspaces() {
  return useQuery<ListWorkspacesResponse>({
    queryKey: [BASE],
    queryFn: () => customFetch<ListWorkspacesResponse>(BASE),
    staleTime: 30 * 1000,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation<{ workspace: WorkspaceSummary }, Error, CreateWorkspaceBody>({
    mutationFn: (body) =>
      customFetch<{ workspace: WorkspaceSummary }>(BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BASE] });
    },
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation<
    { workspace: WorkspaceSummary },
    Error,
    { id: number; data: UpdateWorkspaceBody }
  >({
    mutationFn: ({ id, data }) =>
      customFetch<{ workspace: WorkspaceSummary }>(`${BASE}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BASE] });
    },
  });
}

export function useArchiveWorkspace() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: number }>({
    mutationFn: ({ id }) =>
      customFetch<{ success: boolean }>(`${BASE}/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BASE] });
    },
  });
}

/**
 * Upload (or replace) a workspace's logo image.
 *
 * Multipart/form-data POST to /api/workspaces/:id/logo. The server stores
 * the file in App Storage and persists the public URL on workspaces.logo_url
 * — refetching the workspace list (invalidated below) is enough to refresh
 * every consumer (workspace switcher, business profiles list, etc.).
 *
 * We deliberately do NOT use the JSON-oriented `customFetch` helper because
 * it sets `Content-Type: application/json` for string bodies, which would
 * collide with the boundary header that the browser must inject for
 * multipart payloads.
 */
export function useUploadWorkspaceLogo() {
  const qc = useQueryClient();
  return useMutation<
    { workspace: WorkspaceSummary },
    Error,
    { id: number; file: File }
  >({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BASE}/${id}/logo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        let message = `Upload failed (${res.status})`;
        try {
          const body = await res.json() as { error?: string };
          if (body && typeof body.error === "string") message = body.error;
        } catch {
          /* response was not JSON; keep default message */
        }
        throw new Error(message);
      }
      return res.json() as Promise<{ workspace: WorkspaceSummary }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BASE] });
    },
  });
}

// ─── Workspace entitlements ───────────────────────────────────────────────────

export type WorkspaceEntitlement = {
  id: number;
  userId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  stripeProductId: string | null;
  plan: string;
  status: string;
  workspaceId: number | null;
  gracePeriodEndsAt: string | null;
  activatedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
};

export type GetWorkspaceEntitlementsResponse = {
  entitlements: WorkspaceEntitlement[];
};

const BILLING_BASE = "/api/billing";

/**
 * Fetch all workspace entitlement records for the authenticated user.
 * Used by BusinessProfilesSection to gate the "Add workspace" creation form
 * vs. the upgrade modal without relying solely on frontend state.
 */
export function useGetWorkspaceEntitlements() {
  return useQuery<GetWorkspaceEntitlementsResponse>({
    queryKey: ["/api/billing/workspace-entitlements"],
    queryFn: () =>
      customFetch<GetWorkspaceEntitlementsResponse>(
        `${BILLING_BASE}/workspace-entitlements`,
      ),
    staleTime: 30 * 1000,
  });
}

export type AddWorkspaceCheckoutResponse = {
  url: string;
  sessionId: string;
  requiresSetup?: boolean;
};

export type AddWorkspaceCheckoutInput = {
  /** Display name for the workspace to be created (required, max 80 chars). */
  workspaceName: string;
  /** Optional brand colour hex (e.g. "#2563eb"). */
  brandColor?: string | null;
};

/**
 * Start a Stripe Checkout session for an additional workspace subscription.
 * workspaceName is passed through Stripe metadata so the webhook can
 * auto-provision the workspace without a second manual step.
 * On success the caller should redirect to `data.url`.  When the product is
 * not yet configured the server returns `requiresSetup: true` (HTTP 400).
 */
export function useAddWorkspaceCheckout() {
  return useMutation<AddWorkspaceCheckoutResponse, Error, AddWorkspaceCheckoutInput>({
    mutationFn: (input) =>
      customFetch<AddWorkspaceCheckoutResponse>(
        `${BILLING_BASE}/add-workspace`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        },
      ),
  });
}

export type WorkspaceCreationStatusResponse = {
  status: "pending" | "created";
  workspaceId: number | null;
  workspaceName: string | null;
};

/**
 * Check whether the webhook has auto-provisioned a workspace for the given
 * Stripe checkout session.  Called by WorkspaceSuccess to decide when to
 * redirect the user to Business Profiles.
 */
export function useWorkspaceCreationStatus(sessionId: string | null) {
  return useQuery<WorkspaceCreationStatusResponse>({
    queryKey: ["/api/billing/workspace-creation-status", sessionId],
    queryFn: () =>
      customFetch<WorkspaceCreationStatusResponse>(
        `${BILLING_BASE}/workspace-creation-status?session_id=${encodeURIComponent(sessionId ?? "")}`,
      ),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      // Poll every 2 s while still pending; stop polling once created
      const data = query.state.data;
      if (data?.status === "created") return false;
      return 2000;
    },
    staleTime: 0,
  });
}

export function useActivateWorkspace() {
  const qc = useQueryClient();
  return useMutation<
    { activeWorkspaceId: number; workspace: WorkspaceSummary },
    Error,
    { id: number }
  >({
    mutationFn: ({ id }) =>
      customFetch<{ activeWorkspaceId: number; workspace: WorkspaceSummary }>(
        `${BASE}/${id}/activate`,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      // 1. Update the per-tab active workspace id BEFORE invalidation/reload
      //    so the next request from this tab carries the correct
      //    X-Workspace-Id header.
      if (_activeWorkspaceSink && data.activeWorkspaceId) {
        try {
          _activeWorkspaceSink(data.activeWorkspaceId);
        } catch {
          /* sink failures are non-fatal */
        }
      }
      // 2. Invalidate ALL queries: every cached value belongs to the
      //    previously active workspace and must be re-fetched.
      void qc.invalidateQueries();
      // 3. Force a full page reload — many screens read data via imperative
      //    apiFetch / useEffect outside React Query (e.g. Team performance),
      //    so a router-level refresh is the only reliable way to make every
      //    page reflect the newly active workspace immediately.
      if (typeof window !== "undefined") {
        setTimeout(() => window.location.reload(), 50);
      }
    },
  });
}
